
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { RouteContext } from './useRouteContext';

export interface SavedPosition {
  x: number;
  y: number;
}

export interface SavedPositions {
  [contentId: string]: SavedPosition;
}

export const useSimplePositionPersistence = (context: RouteContext) => {
  const { user } = useAuth();

  // Load positions from database - simple, no state management
  const loadPositions = useCallback(async (): Promise<SavedPositions> => {
    if (!user || !context.contextType || !context.contextId) {
      console.log('[SIMPLE PERSISTENCE] Cannot load - missing user or context');
      return {};
    }

    try {
      console.log(`[SIMPLE PERSISTENCE] Loading positions for ${context.contextType}:${context.contextId}`);
      
      const { data, error } = await supabase
        .from('content_positions')
        .select('content_id, x_position, y_position')
        .eq('user_id', user.id)
        .eq('context_type', context.contextType)
        .eq('context_id', context.contextId);

      if (error) {
        console.error('[SIMPLE PERSISTENCE] Error loading positions:', error);
        return {};
      }

      const positions = data.reduce((acc, pos) => ({
        ...acc,
        [pos.content_id]: { 
          x: Number(pos.x_position), 
          y: Number(pos.y_position) 
        }
      }), {});

      console.log(`[SIMPLE PERSISTENCE] Loaded ${Object.keys(positions).length} positions`);
      return positions;
    } catch (error) {
      console.error('[SIMPLE PERSISTENCE] Failed to load positions:', error);
      return {};
    }
  }, [user, context.contextType, context.contextId]);

  // Save position immediately - no debouncing, no complex state
  const savePosition = useCallback(async (contentId: string, x: number, y: number) => {
    if (!user || !context.contextType || !context.contextId) {
      console.log('[SIMPLE PERSISTENCE] Cannot save - missing user or context');
      return;
    }

    try {
      console.log(`[SIMPLE PERSISTENCE] Immediately saving ${contentId} at (${x}, ${y})`);
      
      const { error } = await supabase
        .from('content_positions')
        .upsert({
          content_id: contentId,
          user_id: user.id,
          context_type: context.contextType!,
          context_id: context.contextId!,
          x_position: x,
          y_position: y,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'content_id,context_id,context_type,user_id'
        });

      if (error) {
        console.error('[SIMPLE PERSISTENCE] Error saving position:', error);
      } else {
        console.log(`[SIMPLE PERSISTENCE] Successfully saved ${contentId}`);
      }
    } catch (error) {
      console.error('[SIMPLE PERSISTENCE] Failed to save position:', error);
    }
  }, [user, context.contextType, context.contextId]);

  // Delete position when content is removed
  const deletePosition = useCallback(async (contentId: string) => {
    if (!user || !context.contextType || !context.contextId) {
      return;
    }

    try {
      const { error } = await supabase
        .from('content_positions')
        .delete()
        .eq('content_id', contentId)
        .eq('user_id', user.id)
        .eq('context_type', context.contextType)
        .eq('context_id', context.contextId);

      if (error) {
        console.error('[SIMPLE PERSISTENCE] Error deleting position:', error);
      } else {
        console.log(`[SIMPLE PERSISTENCE] Deleted position for ${contentId}`);
      }
    } catch (error) {
      console.error('[SIMPLE PERSISTENCE] Failed to delete position:', error);
    }
  }, [user, context.contextType, context.contextId]);

  return {
    loadPositions,
    savePosition,
    deletePosition
  };
};
